import React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, RotateCcw } from "lucide-react";

// ---- localStorage preference helpers (reuse across tables) ----------------
const readLS = (k, fb) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; }
  catch (_) { return fb; }
};
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) { /* noop */ } };
const delLS = (k) => { try { localStorage.removeItem(k); } catch (_) { /* noop */ } };

const EMPTY_SORT = { field: null, dir: null, type: "string" };

// Click-to-sort for any column, persisted per table+user via `storageKey`.
// 1st click = A→Z / low→high, 2nd = Z→A / high→low, 3rd = clear.
export function useTableSort(storageKey) {
  const key = storageKey ? `cc-sort:${storageKey}` : null;
  const [sort, setSort] = React.useState(() => (key ? readLS(key, EMPTY_SORT) : EMPTY_SORT));
  React.useEffect(() => { if (key) writeLS(key, sort); }, [key, sort]);
  const toggle = (field, type = "string") => {
    setSort((s) => {
      if (s.field !== field) return { field, dir: "asc", type };
      if (s.dir === "asc") return { field, dir: "desc", type };
      return { ...EMPTY_SORT };
    });
  };
  return { sort, toggle, setSort };
}

const baseCmp = (a, b, type) => {
  if (type === "number") return Number(a) - Number(b);
  if (type === "bool") return (a ? 1 : 0) - (b ? 1 : 0);
  if (type === "date") return new Date(a).getTime() - new Date(b).getTime();
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
};

const isEmpty = (v) => v === null || v === undefined || v === "" ||
  (typeof v === "number" && Number.isNaN(v));

// Returns a NEW sorted array; empty values always sink to the bottom.
export function sortRows(rows, sort, accessor) {
  if (!sort.field || !sort.dir) return rows;
  const arr = [...rows];
  arr.sort((x, y) => {
    const a = accessor(x, sort.field);
    const b = accessor(y, sort.field);
    const ea = isEmpty(a);
    const eb = isEmpty(b);
    if (ea && eb) return 0;
    if (ea) return 1;
    if (eb) return -1;
    const r = baseCmp(a, b, sort.type);
    return sort.dir === "asc" ? r : -r;
  });
  return arr;
}

export function SortCaret({ field, type = "string", sort, toggle, testId }) {
  const active = sort.field === field;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggle(field, type); }}
      className={`ml-1 inline-flex align-middle shrink-0 transition-colors ${
        active ? "text-[hsl(var(--oxblood))]" : "text-muted-foreground/40 hover:text-muted-foreground"
      }`}
      title={active ? (sort.dir === "asc" ? "Sorted A→Z · click for Z→A" : "Sorted Z→A · click to clear") : "Sort"}
      data-testid={testId}
    >
      <Icon size={12} />
    </button>
  );
}

// ---- Manual (drag) order, persisted per table+user -----------------------
export function useManualOrder(storageKey) {
  const key = storageKey ? `cc-order:${storageKey}` : null;
  const [order, setOrderState] = React.useState(() => (key ? readLS(key, []) : []));
  const setOrder = (ids) => { setOrderState(ids); if (key) writeLS(key, ids); };
  const reset = () => { setOrderState([]); if (key) delLS(key); };
  return { order, setOrder, reset };
}

// Apply a saved id-order to live rows. New records append at the end;
// deleted records are skipped — so it survives data changes gracefully.
export function applyManualOrder(rows, order) {
  if (!order || order.length === 0) return rows;
  const map = new Map(rows.map((r) => [r.id, r]));
  const ordered = order.map((id) => map.get(id)).filter(Boolean);
  const seen = new Set(order);
  const appended = rows.filter((r) => !seen.has(r.id));
  return [...ordered, ...appended];
}

export function reorderIds(currentIds, dragId, targetId) {
  const ids = [...currentIds];
  const from = ids.indexOf(dragId);
  if (from < 0) return ids;
  ids.splice(from, 1);
  const at = targetId === "__end" ? ids.length : ids.indexOf(targetId);
  ids.splice(at < 0 ? ids.length : at, 0, dragId);
  return ids;
}

// Native HTML5 row drag (mirrors the Weekly board pattern). Rows become
// draggable only after the grip handle is grabbed, so inputs stay usable.
export function useRowDrag(enabled, displayIds, onReorder) {
  const [armedId, setArmedId] = React.useState(null);
  const [dragId, setDragId] = React.useState(null);
  const [overId, setOverId] = React.useState(null);
  const end = () => { setArmedId(null); setDragId(null); setOverId(null); };
  const drop = (targetId) => {
    if (!dragId || dragId === targetId) { end(); return; }
    onReorder(reorderIds(displayIds, dragId, targetId));
    end();
  };
  const rowProps = (id) => ({
    draggable: enabled && armedId === id,
    onDragStart: (e) => {
      setDragId(id);
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", id); } catch (_) { /* noop */ }
    },
    onDragOver: (e) => { if (!dragId) return; e.preventDefault(); e.stopPropagation(); setOverId(id); },
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); drop(id); },
    onDragEnd: end,
    className: `${dragId === id ? "opacity-40" : ""} ${overId === id ? "bg-[hsl(var(--oxblood)/0.12)]" : ""}`,
  });
  const handleProps = (id) => ({
    onMouseDown: () => setArmedId(id),
    onMouseUp: () => { if (!dragId) setArmedId(null); },
    onTouchStart: () => setArmedId(id),
  });
  return { dragId, rowProps, handleProps };
}

// Compact status line: "Sorted by Price ↓ · showing 8 of 12" (+ reset order).
export function SortStatus({ sort, manual, labelFor, shown, total, onResetOrder }) {
  let text;
  if (manual) {
    text = <>MANUAL ORDER · showing {shown}</>;
  } else if (sort.field && sort.dir) {
    text = <>SORTED BY {labelFor(sort.field)} {sort.dir === "asc" ? "↑" : "↓"} · showing {shown}{shown !== total ? ` of ${total}` : ""}</>;
  } else {
    text = <>showing {shown}{shown !== total ? ` of ${total}` : ""}</>;
  }
  return (
    <div className="flex items-center gap-3 mono text-[10px] tracking-widest uppercase text-muted-foreground" data-testid="sort-status">
      <span>{text}</span>
      {manual && (
        <button
          type="button"
          onClick={onResetOrder}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          data-testid="reset-order"
          title="Restore default order"
        >
          <RotateCcw size={10} /> RESET ORDER
        </button>
      )}
    </div>
  );
}
